import { create } from 'zustand';
import { api } from '@/api/client';
import type { DashboardLayout } from '@/screens/dashboard/layout';

export interface Preferences {
  language: string;
  uiScale: number;
  fontSize: number;
  targetSize: string;
  rowDensity: string;
  highContrast: boolean;
  underlineLinks: boolean;
  reducedMotion: boolean;
  windowMode: string;
  /** Which home-dashboard cards and sections show, and in what order. Null is
   *  the designed layout, which is also what a new widget joins. */
  dashboardLayout: DashboardLayout | null;
}

export type PreferenceKey = keyof Preferences;

export const DEFAULT_PREFERENCES: Preferences = {
  language: 'en',
  uiScale: 1,
  fontSize: 14,
  targetSize: 'normal',
  rowDensity: 'normal',
  highContrast: false,
  underlineLinks: false,
  reducedMotion: false,
  windowMode: 'same',
  dashboardLayout: null,
};

/**
 * How long a burst of changes waits before it is saved. A slider sends a
 * change on every step it passes; saving each one wrote an audit row per
 * step, and replies arriving out of order put back a value the user had
 * already moved past.
 */
export const SAVE_DELAY_MS = 400;

/**
 * Applies preferences to the document root.
 *
 * Everything is a CSS custom property or a data attribute, so a change is one
 * style recalculation and no component re-renders. That is the whole reason the
 * token layer exists: the interface can be rescaled without any component
 * knowing it happened.
 */
export function applyPreferences(p: Preferences): void {
  const root = document.documentElement;

  root.style.setProperty('--ui-scale', String(p.uiScale));
  root.style.setProperty('--font-size-base', `${p.fontSize}px`);

  root.dataset.targetSize = p.targetSize;
  root.dataset.density = p.rowDensity;
  root.dataset.contrast = p.highContrast ? 'high' : 'normal';
  root.dataset.underlineLinks = String(p.underlineLinks);
  root.dataset.reducedMotion = String(p.reducedMotion);

  // Drives both the Nastaliq font stack and right-to-left layout.
  root.lang = p.language;
  root.dir = p.language === 'ur' ? 'rtl' : 'ltr';
}

/**
 * Every window of the app shares one set of preferences. With "Open in a new
 * window" on, someone may have four windows open; a change made in one must
 * reach the rest, or each window looks different until it is reloaded.
 */
const channel: BroadcastChannel | null =
  typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('poultry.preferences');

interface PreferencesState {
  prefs: Preferences;
  saving: boolean;
  error: string | null;
  /** When the server last confirmed a save, for the status line. */
  savedAt: Date | null;

  /** Applies immediately, then saves after a short pause. */
  set: (patch: Partial<Preferences>) => void;
  /** Puts the given settings back to their defaults. */
  resetKeys: (keys: PreferenceKey[]) => void;
  /** Tries the last failed save again. */
  retry: () => void;
  /** Adopts what the server returned at sign-in, without saving it back. */
  hydrate: (prefs: Partial<Preferences> | null) => void;
  /**
   * Back to defaults on this screen only, without saving — used at sign-out,
   * so the next person at the sign-in screen does not get the last person's
   * text size, language or contrast.
   */
  clear: () => void;
}

let pending: Partial<Preferences> = {};
let timer: ReturnType<typeof setTimeout> | null = null;
/** Counts saves, so only the reply to the newest one is applied. */
let generation = 0;

export const usePreferences = create<PreferencesState>((setState, get) => {
  async function flush() {
    timer = null;
    const patch = pending;
    pending = {};
    if (Object.keys(patch).length === 0) return;

    const mine = ++generation;
    setState({ saving: true, error: null });
    try {
      const saved = await api.patch<Preferences>('/api/v1/settings/preferences', patch);
      // A newer change is already on screen or on its way; this reply would
      // put back an older value.
      if (mine !== generation || timer !== null) return;
      applyPreferences(saved);
      setState({ prefs: saved, saving: false, savedAt: new Date() });
      channel?.postMessage(saved);
    } catch (e) {
      if (mine !== generation) return;
      // Kept so Retry can send it again. The change stays on screen: quietly
      // undoing what the user just did is worse than saying it was not saved.
      pending = { ...patch, ...pending };
      setState({
        saving: false,
        error: e instanceof Error ? `Not saved: ${e.message}` : 'Not saved',
      });
    }
  }

  function schedule() {
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => void flush(), SAVE_DELAY_MS);
  }

  return {
    prefs: DEFAULT_PREFERENCES,
    saving: false,
    error: null,
    savedAt: null,

    set: (patch) => {
      const next = { ...get().prefs, ...patch };
      applyPreferences(next);
      pending = { ...pending, ...patch };
      setState({ prefs: next, error: null });
      schedule();
    },

    resetKeys: (keys) => {
      const patch: Partial<Preferences> = {};
      for (const key of keys) (patch as Record<string, unknown>)[key] = DEFAULT_PREFERENCES[key];
      get().set(patch);
    },

    retry: () => {
      if (Object.keys(pending).length > 0) void flush();
    },

    hydrate: (prefs) => {
      const merged = { ...DEFAULT_PREFERENCES, ...(prefs ?? {}) };
      applyPreferences(merged);
      setState({ prefs: merged });
    },

    clear: () => {
      if (timer !== null) clearTimeout(timer);
      timer = null;
      pending = {};
      generation++;
      applyPreferences(DEFAULT_PREFERENCES);
      setState({
        prefs: DEFAULT_PREFERENCES,
        saving: false,
        error: null,
        savedAt: null,
      });
    },
  };
});

channel?.addEventListener('message', (e: MessageEvent<Preferences>) => {
  // A change still waiting to be saved here wins over another window's.
  if (timer !== null) return;
  usePreferences.getState().hydrate(e.data);
});

/**
 * Which settings differ from their default.
 *
 * The dashboard layout is left out: it is not a single value with a default to
 * compare against, and Settings › Dashboard has its own "Put back the default".
 */
export function changedKeys(prefs: Preferences): PreferenceKey[] {
  return (Object.keys(DEFAULT_PREFERENCES) as PreferenceKey[]).filter(
    (k) => k !== 'dashboardLayout' && prefs[k] !== DEFAULT_PREFERENCES[k],
  );
}
